/**
 * @file Studio.jsx
 * @description Main Studio component for content authoring
 * Refactored to use custom hooks, services, and sub-components
 */

import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { parseVirtualBlocksFromPages } from "../../utils/virtual-blocks";
import { useAppMode } from "../../utils/tabFiltering";
import { ENGLISH, ARABIC } from "../../utils/ocr";
import { useStore } from "../../store/store";

import { DEFAULTS, LANGUAGE_CODES } from "./constants";
import { StudioHeader, StudioLayout } from "./components";

// Import hooks
import usePageNavigation from "./hooks/usePageNavigation";
import useAreaManagement from "./hooks/useAreaManagement";
import useCompositeBlocks from "./hooks/useCompositeBlocks";
import useStudioActions from "./hooks/useStudioActions";
import useVirtualBlocks from "./hooks/useVirtualBlocks";
import useLabelManagement from "./hooks/useLabelManagement";
import usePlayBlock from "./hooks/usePlayBlock";
import useStudioColumns from "./hooks/useStudioColumns";
import useReaderNarration from "./hooks/useReaderNarration";
import ReaderAudioContext from "./context/ReaderAudioContext";

/**
 * Studio Component - Content authoring tool for book pages
 * @param {Object} props - Component props
 */
const Studio = (props) => {
  const {
    pages,
    setPages,
    type,
    subObject,
    types,
    handleSubmit,
    language: langProp,
    typeOfActiveType: tOfActiveType,
    onSubmitAutoGenerate,
    loadingAutoGenerate,
    onSelectFromLibrary,
    refetch,
    compositeBlocksTypes,
  } = props;

  // ============ REFS ============
  const studioEditorRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const thumbnailsRef = React.useRef(null);
  const recalculateAreasRef = React.useRef(null);
  const pageContainerRef = React.useRef(null);

  // ============ ROUTER & MODE ============
  const { chapterId } = useParams();
  const location = useLocation();
  const mode = useAppMode();
  const isReaderMode = mode === "reader";

  // ============ GLOBAL STORE ============
  const { openModal, setFormState } = useStore();

  // ============ LOCAL STATE ============
  const [language, setLanguage] = React.useState(
    langProp === LANGUAGE_CODES.ENGLISH ? ENGLISH : ARABIC
  );
  const [virtualBlocks, setVirtualBlocks] = React.useState(() =>
    subObject ? [] : parseVirtualBlocksFromPages(pages)
  );

  // `pages` can arrive after mount (e.g. ScanAndUpload starts with an empty
  // array while its query is loading, then swaps in the fetched pages) or
  // gain/lose entries later (add/import/delete page). The lazy useState
  // initializer above only runs once, so keep virtualBlocks' length aligned
  // with pages here — otherwise virtualBlocks[activePageIndex] goes
  // undefined and crashes formatVirtualBlocksForSubmission on submit.
  React.useEffect(() => {
    if (subObject) return;
    setVirtualBlocks((prev) => {
      if (prev.length === pages.length) return prev;
      const parsed = parseVirtualBlocksFromPages(pages);
      return pages.map((_, idx) => prev[idx] ?? parsed[idx]);
    });
  }, [pages, subObject]);
  const [showStickyToolbar, setShowStickyToolbar] = React.useState(false);
  const [imageScaleFactor, setImageScaleFactor] = React.useState(
    DEFAULTS.IMAGE_SCALE_FACTOR
  );
  const [showBlocksStyling, setShowBlocksStyling] = React.useState(true);
  const [isWhiteOutMode, setIsWhiteOutMode] = React.useState(false);
  const onToggleWhiteOutMode = React.useCallback(
    () => setIsWhiteOutMode((prev) => !prev),
    []
  );

  // ============ PAGE NAVIGATION ============
  const insertPageAtRef = React.useRef(null);
  const insertPagesAtRef = React.useRef(null);
  const deletePageAtRef = React.useRef(null);
  const reorderPageAtRef = React.useRef(null);

  const {
    activePageIndex,
    setActivePageIndex,
    activePageId,
    changePageByIndex,
    changePageById,
    addLocalPages,
    addEmptyPage,
    addImportedPages,
    insertPageLocally,
    reorderPages,
  } = usePageNavigation({
    pages,
    setPages,
    insertPageAtRef,
    insertPagesAtRef,
    deletePageAtRef,
    reorderPageAtRef,
    subObject,
  });

  // ============ AREA MANAGEMENT ============
  const {
    areas,
    setAreas,
    areasProperties,
    setAreasProperties,
    deletedDeepBlockAreas,
    addManualWhiteOverlayArea,
    removeWhiteOverlayArea,
    insertPageAt,
    insertPagesAt,
    deletePageAt,
    reorderPageAt,
    getBlockFromBlockId,
    recalculateAreas,
    updateAreaProperty,
    onClickDeleteArea,
    updateAreaPropertyById,
    onEditText,
    syncAreasProperties,
    onChangeArea,
    onClickSubmit,
    loadingSubmit,
  } = useAreaManagement({
    pages,
    setPages,
    activePageIndex,
    types,
    studioEditorRef,
    subObject,
    type,
    handleSubmit,
    updateAreaPropertyForParent: props.updateAreaProperty,
    activePageId,
    virtualBlocks,
    refetch,
    pageContainerRef,
    setShowBlocksStyling,
    chapterId,
  });

  // Keep recalculateAreas ref updated (to avoid dependency in useEffect)
  React.useEffect(() => {
    recalculateAreasRef.current = recalculateAreas;
  }, [recalculateAreas]);

  React.useEffect(() => {
    insertPageAtRef.current = insertPageAt;
  }, [insertPageAt]);

  React.useEffect(() => {
    insertPagesAtRef.current = insertPagesAt;
  }, [insertPagesAt]);

  React.useEffect(() => {
    deletePageAtRef.current = deletePageAt;
  }, [deletePageAt]);

  React.useEffect(() => {
    reorderPageAtRef.current = reorderPageAt;
  }, [reorderPageAt]);

  // ============ VIRTUAL BLOCKS ============
  const { showVB, onClickToggleVirutalBlocks } = useVirtualBlocks({
    virtualBlocks,
    setVirtualBlocks,
    pages,
    subObject,
    recalculateAreas,
  });

  // ============ COMPOSITE BLOCKS ============
  const {
    compositeBlocks,
    activeCompositeBlock,
    totalAreas,
    setCompositeBlocks,
    loadingSubmitCompositeBlocks,
    onChangeCompositeBlocks,
    DeleteCompositeBlocks,
    processCompositeBlock,
    onSubmitCompositeBlocks,
    onChangeCompositeBlockArea,
    onClickHand,
  } = useCompositeBlocks({
    canvasRef,
    studioEditorRef,
    language,
    chapterId,
    openModal,
    pages,
    activePageIndex,
    areasProperties,
    compositeBlocksTypes,
    changePageByIndex,
  });

  // ============ STUDIO ACTIONS ============
  const { highlight, setHighlight, highlightedBlockId, hightBlock } =
    useStudioActions({ getBlockFromBlockId });

  // ============ READER NARRATION ============
  const readerAudio = useReaderNarration({
    pages,
    activePageIndex,
    highlightedBlockId,
    hightBlock,
    contentLanguage: location.state?.contentLanguage,
    enabled: isReaderMode,
  });

  // ============ LABEL MANAGEMENT ============
  const { onChangeLabel } = useLabelManagement({
    areasProperties,
    areas,
    activePageIndex,
    types,
    subObject,
    tOfActiveType,
    canvasRef,
    studioEditorRef,
    language,
    syncAreasProperties,
    updateAreaProperty,
    updateAreaPropertyById,
    openModal,
  });

  // ============ PLAY BLOCK ============
  const { onPlayBlock } = usePlayBlock({ openModal, setFormState });

  // ============ COLUMNS ============

  const onPageDeleted = React.useCallback(
    (pageIndex) => {
      deletePageAt(pageIndex);
      changePageByIndex(Math.max(0, pageIndex - 1));
    },
    [deletePageAt, changePageByIndex]
  );

  // Memoize rightColumnProps to prevent new object reference every render
  const rightColumnProps = React.useMemo(
    () => ({
      areasProperties,
      setAreasProperties,
      addLocalPages,
      onEditText,
      onClickDeleteArea,
      type,
      onClickSubmit,
      loadingSubmit,
      updateAreaProperty,
      updateAreaPropertyById,
      types,
      onChangeLabel,
      subObject,
      tOfActiveType,
      onSubmitAutoGenerate,
      loadingAutoGenerate,
      onSelectFromLibrary,
      onClickToggleVirutalBlocks,
      showVB,
      compositeBlocks,
      totalAreas,
      compositeBlocksTypes,
      onChangeCompositeBlocks,
      processCompositeBlock,
      onSubmitCompositeBlocks,
      loadingSubmitCompositeBlocks,
      DeleteCompositeBlocks,
      highlight,
      setHighlight,
      setActivePageIndex,
      onClickHand,
      showBlocksStyling,
      setShowBlocksStyling,
      isWhiteOutMode,
      onToggleWhiteOutMode,
    }),
    [
      areasProperties,
      setAreasProperties,
      addLocalPages,
      onEditText,
      onClickDeleteArea,
      type,
      onClickSubmit,
      loadingSubmit,
      updateAreaProperty,
      updateAreaPropertyById,
      types,
      onChangeLabel,
      subObject,
      tOfActiveType,
      onSubmitAutoGenerate,
      loadingAutoGenerate,
      onSelectFromLibrary,
      onClickToggleVirutalBlocks,
      showVB,
      compositeBlocks,
      totalAreas,
      compositeBlocksTypes,
      onChangeCompositeBlocks,
      processCompositeBlock,
      onSubmitCompositeBlocks,
      loadingSubmitCompositeBlocks,
      DeleteCompositeBlocks,
      highlight,
      setHighlight,
      setActivePageIndex,
      onClickHand,
      showBlocksStyling,
      setShowBlocksStyling,
      isWhiteOutMode,
      onToggleWhiteOutMode,
    ]
  );

  const {
    leftColumns,
    rightColumns,
    activeLeftTab,
    setActiveLeftTab,
    activeRightTab,
    setActiveRightTab,
  } = useStudioColumns({
    isReaderMode,
    pages,
    setPages,
    activePageIndex,
    chapterId,
    thumbnailsRef,
    changePageByIndex,
    changePageById,
    getBlockFromBlockId,
    hightBlock,
    rightColumnProps,
    onSelectFromLibrary,
    onPageDeleted,
    addEmptyPage,
    addImportedPages,
    insertPageLocally,
    reorderPages,
  });

  // ============ EFFECTS ============

  // Sticky toolbar observer
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyToolbar(!entry.isIntersecting),
      { root: null, threshold: 0 }
    );

    const imageActionsRef = studioEditorRef.current?.imageActionsRef;
    if (imageActionsRef?.current) {
      observer.observe(imageActionsRef.current);
    }

    return () => {
      if (imageActionsRef?.current) {
        observer.unobserve(imageActionsRef.current);
      }
    };
  }, []);

  // Recalculate areas on zoom change
  // Fixed: Guard setAreas to only update when needed, use ref for recalculateAreas
  React.useEffect(() => {
    if (!imageScaleFactor) return;

    setAreas((prev) => {
      const currentPageAreas = prev[activePageIndex];
      if (!currentPageAreas) return prev;

      // Check if any area actually needs the _updated flag reset
      const needsUpdate = currentPageAreas.some(
        (area) => area._updated !== false
      );
      if (!needsUpdate) return prev; // Same reference = no re-render

      // Only create new array when actually needed
      const newAreas = [...prev];
      newAreas[activePageIndex] = currentPageAreas.map((area) => ({
        ...area,
        _updated: false,
      }));
      return newAreas;
    });

    // Use ref to avoid unstable dependency
    const timer = setTimeout(() => recalculateAreasRef.current?.(), 10);
    return () => clearTimeout(timer);
  }, [imageScaleFactor, activePageIndex, setAreas]);

  // ============ HANDLERS ============

  const onChangeHandler = React.useCallback(
    (areasParam) => {
      if (activeRightTab?.id === "composite-blocks") {
        onChangeCompositeBlockArea(areasParam);
      } else {
        onChangeArea(areasParam);
      }
    },
    [activeRightTab, onChangeCompositeBlockArea, onChangeArea]
  );

  const publishLanguageCode =
    language === ARABIC ? LANGUAGE_CODES.ARABIC : LANGUAGE_CODES.ENGLISH;

  const handleSetVirtualBlocks = React.useCallback(
    (value) => {
      setVirtualBlocks((prev) => {
        const newBlocks = [...prev];
        newBlocks[activePageIndex] =
          typeof value === "function"
            ? value(newBlocks[activePageIndex])
            : value;
        return newBlocks;
      });
    },
    [activePageIndex]
  );

  // ============ RENDER ============

  return (
    <ReaderAudioContext.Provider value={readerAudio}>
      <StudioHeader
        showStickyToolbar={showStickyToolbar}
        imageScaleFactor={imageScaleFactor}
        setImageScaleFactor={setImageScaleFactor}
        areas={areas}
        setAreas={setAreas}
        activePageIndex={activePageIndex}
        areasProperties={areasProperties}
        showVB={showVB}
        onClickToggleVirtualBlocks={onClickToggleVirutalBlocks}
        onImageLoad={recalculateAreas}
        pages={pages}
        onClickImage={changePageByIndex}
        language={language}
        setLanguage={setLanguage}
        chapterId={chapterId}
        publishLanguage={publishLanguageCode}
      />

      <StudioLayout
        ref={studioEditorRef}
        pageContainerRef={pageContainerRef}
        leftColumns={leftColumns}
        activeLeftTab={activeLeftTab}
        setActiveLeftTab={setActiveLeftTab}
        rightColumns={rightColumns}
        activeRightTab={activeRightTab}
        setActiveRightTab={setActiveRightTab}
        areasProperties={areasProperties}
        setAreasProperties={setAreasProperties}
        activePageIndex={activePageIndex}
        imageScaleFactor={imageScaleFactor}
        setImageScaleFactor={setImageScaleFactor}
        areas={areas}
        setAreas={setAreas}
        onChangeHandler={onChangeHandler}
        pages={pages}
        onImageLoad={recalculateAreas}
        virtualBlocks={virtualBlocks[activePageIndex]}
        setVirtualBlocks={handleSetVirtualBlocks}
        showVB={showVB}
        onClickToggleVirtualBlocks={onClickToggleVirutalBlocks}
        onClickImage={changePageByIndex}
        compositeBlocksTypes={compositeBlocksTypes}
        compositeBlocks={activeCompositeBlock}
        setCompositeBlocks={setCompositeBlocks}
        highlight={highlight}
        setHighlight={setHighlight}
        highlightedBlockId={highlightedBlockId}
        onPlayBlock={onPlayBlock}
        showBlocksStyling={showBlocksStyling}
        setShowBlocksStyling={setShowBlocksStyling}
        chapterId={chapterId}
        publishLanguage={publishLanguageCode}
        deletedDeepBlockAreas={deletedDeepBlockAreas}
        isWhiteOutMode={isWhiteOutMode}
        onToggleWhiteOutMode={onToggleWhiteOutMode}
        addManualWhiteOverlayArea={addManualWhiteOverlayArea}
        removeWhiteOverlayArea={removeWhiteOverlayArea}
      />

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </ReaderAudioContext.Provider>
  );
};

export default Studio;
